// Pure helpers for the list's "User local time" column. `now` is a parameter so
// the page's existing per-second tick is the only clock.

export interface LocalTimeRow {
  student_timezone?: string | null;
}

export type CallEligibility = 'eligible' | 'not_eligible';

export interface LocalTimeCell {
  time: string;
  place: string;
  call: CallEligibility;
}

// Local hours [start, end) during which the user may be called.
const CALL_START_HOUR = 8;
const CALL_END_HOUR = 21;

// en-US renders these zones as GMT±x; [standard, daylight] where DST applies.
const CURATED_ABBREVS: Record<string, string | [string, string]> = {
  'Asia/Kolkata': 'IST',
  'Asia/Calcutta': 'IST',
  'Asia/Singapore': 'SGT',
  'Asia/Dubai': 'GST',
  'Asia/Manila': 'PHT',
  'Europe/London': ['GMT', 'BST'],
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Hong_Kong': 'HKT',
  'Australia/Sydney': ['AEST', 'AEDT'],
  'Australia/Melbourne': ['AEST', 'AEDT'],
  'Asia/Tokyo': 'JST',
  'Asia/Karachi': 'PKT',
  'Asia/Dhaka': 'BST',
  'Asia/Riyadh': 'AST',
  'Africa/Lagos': 'WAT',
  'Africa/Nairobi': 'EAT'
};

const GMT_FORM = /^GMT(?:([+-])(\d{1,2})(?::?(\d{2}))?)?$/;

export function cityFromZone(zone: string | null | undefined): string {
  if (!zone) return '';
  const parts = zone.split('/');
  return (parts[parts.length - 1] || '').replace(/_/g, ' ');
}

function shortZoneName(zone: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'short'
  }).formatToParts(at);
  return parts.find((p) => p.type === 'timeZoneName')?.value || '';
}

// Minutes east of UTC, parsed from a GMT±x name; null when not that form.
function gmtOffsetMinutes(name: string): number | null {
  const m = GMT_FORM.exec(name);
  if (!m) return null;
  if (!m[1]) return 0;
  const minutes = Number(m[2]) * 60 + Number(m[3] || 0);
  return m[1] === '-' ? -minutes : minutes;
}

export function zoneAbbrev(zone: string | null | undefined, now: number | Date): string {
  if (!zone) return '';
  try {
    const at = new Date(now);
    const name = shortZoneName(zone, at);
    const offset = gmtOffsetMinutes(name);
    if (offset === null) return name;
    const curated = CURATED_ABBREVS[zone];
    if (!curated) return name;
    if (typeof curated === 'string') return curated;
    const year = at.getUTCFullYear();
    const jan = gmtOffsetMinutes(shortZoneName(zone, new Date(Date.UTC(year, 0, 1))));
    const jul = gmtOffsetMinutes(shortZoneName(zone, new Date(Date.UTC(year, 6, 1))));
    if (jan === null || jul === null) return name;
    return offset > Math.min(jan, jul) ? curated[1] : curated[0];
  } catch {
    return '';
  }
}

export function formatLocalTime(zone: string | null | undefined, now: number | Date): string {
  if (!zone) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(new Date(now));
  } catch {
    return '';
  }
}

export function callEligibility(
  zone: string | null | undefined,
  now: number | Date
): CallEligibility | null {
  if (!zone) return null;
  try {
    const hour = Number(
      new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: 'numeric', hourCycle: 'h23' })
        .formatToParts(new Date(now))
        .find((p) => p.type === 'hour')?.value
    );
    if (Number.isNaN(hour)) return null;
    return hour >= CALL_START_HOUR && hour < CALL_END_HOUR ? 'eligible' : 'not_eligible';
  } catch {
    return null;
  }
}

export function localTimeCell(row: LocalTimeRow, now: number | Date): LocalTimeCell | null {
  const zone = row.student_timezone;
  if (!zone) return null;
  const time = formatLocalTime(zone, now);
  const call = callEligibility(zone, now);
  if (!time || !call) return null;
  const abbrev = zoneAbbrev(zone, now);
  const city = cityFromZone(zone);
  return { time, place: abbrev ? `${city} (${abbrev})` : city, call };
}
